// Store dei REMINDERS (promemoria)
import { create } from 'zustand';
import type { Reminder } from '@/types';
import { getDatabase } from '@/lib/db';
import { genId, nowISO } from '@/utils';
import { scheduleReminder, cancelReminder, requestPermission, checkPermission } from '@/lib/notifications';
import { useAuthStore } from './authStore';
import { pushReminderToCloud } from './syncHelpers';
import { getSupabase, isSupabaseConfigured } from '@/lib/supabase';

interface RemindersState {
  reminders: Reminder[];
  loading: boolean;
  load: () => Promise<void>;
  create: (data: Partial<Reminder>) => Promise<Reminder>;
  update: (id: string, patch: Partial<Reminder>) => Promise<void>;
  remove: (id: string) => Promise<void>;
  toggleEnabled: (id: string) => Promise<void>;
}

export const useRemindersStore = create<RemindersState>((set, get) => ({
  reminders: [],
  loading: false,

  load: async () => {
    set({ loading: true });
    try {
      // Richiede permesso notifiche esplicitamente all'avvio
      const permOk = await checkPermission();
      if (!permOk) {
        // Primo avvio: chiedi permesso in background (no bloccante)
        await requestPermission().catch(() => {});
      }
      const db = getDatabase();
      const reminders = await db.getAllReminders();
      set({ reminders, loading: false });
      // Rischedula tutte le notifiche
      for (const r of reminders) {
        if (r.enabled) await scheduleReminder(r);
        else await cancelReminder(r.id);
      }
    } catch (e) {
      console.error('[reminders] load error', e);
      set({ loading: false });
    }
  },

  create: async (data) => {
    const now = nowISO();
    const r: Reminder = {
      id: genId(),
      note_id: data.note_id ?? null,
      title: data.title ?? 'Promemoria',
      body: data.body ?? '',
      time: data.time ?? null,
      days_of_week: data.days_of_week ?? [],
      at: data.at ?? null,
      enabled: data.enabled ?? true,
      created_at: now,
      updated_at: now,
    };
    const db = getDatabase();
    await db.upsertReminder(r);
    if (r.enabled) await scheduleReminder(r);
    set({ reminders: [r, ...get().reminders] });
    // Sync cloud (best effort)
    pushReminderToCloud(r).catch(() => {});
    return r;
  },

  update: async (id, patch) => {
    const current = get().reminders.find((r) => r.id === id);
    if (!current) return;
    const updated: Reminder = { ...current, ...patch, updated_at: nowISO() };
    const db = getDatabase();
    await db.upsertReminder(updated);
    // Rischedula notifica
    await cancelReminder(id);
    if (updated.enabled) await scheduleReminder(updated);
    set({ reminders: get().reminders.map((r) => (r.id === id ? updated : r)) });
    pushReminderToCloud(updated).catch(() => {});
  },

  remove: async (id) => {
    const db = getDatabase();
    await db.deleteReminder(id);
    await cancelReminder(id);
    set({ reminders: get().reminders.filter((r) => r.id !== id) });
    // Best effort cloud delete
    const user = useAuthStore.getState().user;
    if (user && isSupabaseConfigured()) {
      try {
        const c = getSupabase();
        await c.from('reminders').delete().eq('id', id).eq('user_id', user.id);
      } catch { /* ignore */ }
    }
  },

  toggleEnabled: async (id) => {
    const r = get().reminders.find((x) => x.id === id);
    if (r) await get().update(id, { enabled: !r.enabled });
  },
}));
